import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  type XAxisTickContentProps,
} from 'recharts';
import {
  Activity,
  Zap,
  Radio,
  AlertTriangle,
  GitBranch,
  ShieldCheck,
  Link2,
  Globe,
} from 'lucide-react';
import { FlagIcon } from '@/components/FlagIcon';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { motion } from 'framer-motion';
import { KPICard } from './KPICard';
import { ChartCard } from './ChartCard';
import { fetchPipelineData } from '../api';
import type { DashboardFilters } from '../DashboardPage';

const STATUS_COLORS: Record<string, string> = {
  waiting_for_client_brief: '#6b7280',
  asset_in_development: '#64748b',
  ad_copy_creation: '#3b82f6',
  activation_in_setup: '#22c55e',
  waiting_for_preview_approval: '#f97316',
  preview_approved: '#10b981',
  campaign_live: '#9333ea',
};

const STATUS_LABELS: Record<string, string> = {
  waiting_for_client_brief: '1. Waiting for Client Brief',
  asset_in_development: '2. Asset in Development',
  ad_copy_creation: '3. Ad Copy Creation',
  activation_in_setup: '4. Activation in Setup',
  waiting_for_preview_approval: '5. Waiting for Preview Approval',
  preview_approved: '6. Preview Approved',
  campaign_live: '7. Campaign Live',
};

const STATUS_DESCRIPTIONS: Record<string, string> = {
  waiting_for_client_brief: 'Awaiting FordShare Activity ID',
  asset_in_development: 'FordShare Activity ID has been set',
  ad_copy_creation: 'FordShare Activity ID set and at least one asset saved',
  activation_in_setup: 'Asset approved, activation being configured',
  waiting_for_preview_approval: 'Preview link added, awaiting Local Ford approval',
  preview_approved: 'Preview approved, ready for launch',
  campaign_live: 'Campaign is live and running',
};

const STATUS_ORDER = [
  'waiting_for_client_brief',
  'asset_in_development',
  'ad_copy_creation',
  'activation_in_setup',
  'waiting_for_preview_approval',
  'preview_approved',
  'campaign_live',
];

const SLA_COLORS = {
  on_track: '#22c55e',
  warning: '#f59e0b',
  breached: '#ef4444',
};

function renderBarLabel(totalCampaigns: number, data: { status: string }[]) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (props: any) => {
    const { x, y, width, height, value, index } = props;
    if (value == null || !totalCampaigns) return null;
    const pct = ((value / totalCampaigns) * 100).toFixed(1);
    const status = data[index]?.status;
    const color = STATUS_COLORS[status] || '#94a3b8';
    return (
      <text
        x={(x ?? 0) + (width ?? 0) + 8}
        y={(y ?? 0) + (height ?? 0) / 2}
        fill={color}
        fontSize={12}
        fontWeight={600}
        dominantBaseline="central"
      >
        {value.toLocaleString()} ({pct}%)
      </text>
    );
  };
}

function renderYAxisTick(data: { status: string; label: string }[]) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (props: any) => {
    const { x, y, index } = props;
    const entry = data[index];
    if (!entry) return null;
    const color = STATUS_COLORS[entry.status] || '#94a3b8';
    return (
      <text
        x={x}
        y={y}
        fill={color}
        fontSize={12}
        fontWeight={500}
        textAnchor="end"
        dominantBaseline="central"
      >
        {entry.label}
      </text>
    );
  };
}

interface PipelineTabProps {
  filters: DashboardFilters;
}

export function PipelineTab({ filters }: PipelineTabProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'pipeline', filters],
    queryFn: () =>
      fetchPipelineData({
        campaign_status: filters.campaignStatus,
        market: filters.market || undefined,
      }),
    staleTime: 2 * 60_000,
  });

  const overview = data?.overview;
  const sla = data?.sla_summary;
  const total = overview?.total ?? 0;

  const statusBreakdown = STATUS_ORDER.map((status) => {
    const item = data?.status_breakdown?.find((s) => s.status === status);
    return {
      status,
      label: STATUS_LABELS[status] || status,
      count: item?.count ?? 0,
    };
  });

  const slaTotal = sla ? sla.on_track + sla.warning + sla.breached : 0;

  const slaSegments = sla
    ? [
        { name: 'On Track', value: sla.on_track, color: SLA_COLORS.on_track },
        { name: 'Warning', value: sla.warning, color: SLA_COLORS.warning },
        { name: 'Breached', value: sla.breached, color: SLA_COLORS.breached },
      ].filter((d) => d.value > 0)
    : [];

  const marketBreakdown = data?.market_breakdown ?? [];

  const fordsharePercent =
    overview && overview.fordshare_total > 0
      ? Math.round((overview.fordshare_coverage / overview.fordshare_total) * 100)
      : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400" />
      </div>
    );
  }

  return (
    <div className="flex flex-col p-6 gap-5 overflow-hidden" style={{ height: '100%' }}>
      {/* KPI Row */}
      <div key={`kpis-${filters.animationKey}`} className="grid grid-cols-5 gap-4 flex-shrink-0">
        {[
          <KPICard
            key="total"
            icon={Activity}
            label="Total Campaigns"
            value={overview?.total?.toLocaleString() ?? '0'}
            subtext="Matching current filters"
            tooltip="Total campaigns from approved media plans, excluding archived"
          />,
          <KPICard
            key="progress"
            icon={Zap}
            label="In Progress"
            value={overview?.in_progress?.toLocaleString() ?? '0'}
            subtext="Past initial stage"
            tooltip="Campaigns that have moved beyond 'Waiting for Client Brief'"
          />,
          <KPICard
            key="live"
            icon={Radio}
            label="Live"
            value={overview?.live?.toLocaleString() ?? '0'}
            subtext="Currently running"
            tooltip="Campaigns marked as live"
          />,
          <KPICard
            key="expired"
            icon={AlertTriangle}
            label="Expired"
            value={overview?.expired?.toLocaleString() ?? '0'}
            subtext="Missed deadline"
            tooltip="Campaigns that passed their end date without going live"
          />,
          <KPICard
            key="fordshare"
            icon={Link2}
            label="FordShare IDs"
            value={`${fordsharePercent}%`}
            subtext={`${overview?.fordshare_coverage ?? 0} of ${overview?.fordshare_total ?? 0}`}
            tooltip="Campaigns with a FordShare Activity ID set, enabling asset work"
          />,
        ].map((card, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.06, ease: [0.4, 0, 0.2, 1] }}
          >
            {card}
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="flex-1 min-h-0 flex gap-5">
        {/* Status Funnel */}
        <div className="flex-[3]">
          <ChartCard
            icon={GitBranch}
            title="Campaign Pipeline"
            subtitle="Status progression across all campaigns"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={statusBreakdown}
                layout="vertical"
                margin={{ top: 4, right: 80, left: 8, bottom: 4 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#cbd5e1"
                  strokeWidth={1.5}
                  horizontal={false}
                />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: '#94a3b8' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  dataKey="label"
                  type="category"
                  width={220}
                  tick={renderYAxisTick(statusBreakdown)}
                  axisLine={false}
                  tickLine={false}
                  tickMargin={12}
                />
                <RechartsTooltip
                  cursor={{ fill: 'rgba(0, 20, 204, 0.04)' }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const entry = payload[0].payload as {
                      status: string;
                      label: string;
                      count: number;
                    };
                    const pct = total > 0 ? ((entry.count / total) * 100).toFixed(1) : '0.0';
                    const color = STATUS_COLORS[entry.status] || '#94a3b8';
                    return (
                      <div className="bg-[#0014cc] text-white rounded-lg px-3.5 py-2.5 shadow-lg max-w-[240px]">
                        <div className="flex items-center gap-2 mb-1.5">
                          <div
                            className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                            style={{ backgroundColor: color }}
                          />
                          <span className="font-semibold text-xs">{entry.label}</span>
                        </div>
                        <p className="text-[11px] text-blue-200 mb-2 leading-snug">
                          {STATUS_DESCRIPTIONS[entry.status]}
                        </p>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-sm font-bold">{entry.count.toLocaleString()}</span>
                          <span className="text-[11px] text-blue-200">campaigns</span>
                          <span className="text-[11px] text-blue-200 ml-auto">{pct}%</span>
                        </div>
                      </div>
                    );
                  }}
                />
                <Bar
                  dataKey="count"
                  radius={[0, 6, 6, 0]}
                  maxBarSize={28}
                  label={{ content: renderBarLabel(total, statusBreakdown) }}
                >
                  {statusBreakdown.map((entry) => (
                    <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || '#94a3b8'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Right column — SLA + Placeholder stacked */}
        <div className="flex-[2] flex flex-col gap-5">
          {/* SLA Health — compact */}
          <div className="flex-shrink-0">
            <ChartCard
              icon={ShieldCheck}
              title="SLA Health"
              subtitle={`${slaTotal} campaigns tracked`}
            >
              <div key={filters.animationKey} className="flex flex-col justify-center gap-4">
                {slaTotal > 0 ? (
                  <div className="flex rounded-full h-5 overflow-hidden">
                    {slaSegments.map((entry, i) => (
                      <Tooltip key={entry.name}>
                        <TooltipTrigger asChild>
                          <motion.div
                            className="h-full first:rounded-l-full last:rounded-r-full cursor-default"
                            initial={{ width: 0 }}
                            animate={{ width: `${(entry.value / slaTotal) * 100}%` }}
                            transition={{
                              duration: 0.6,
                              delay: i * 0.1 + 0.3,
                              ease: [0.4, 0, 0.2, 1],
                            }}
                            style={{ backgroundColor: entry.color }}
                          />
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {entry.name}: {entry.value} ({((entry.value / slaTotal) * 100).toFixed(1)}
                          %)
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                ) : (
                  <div className="h-5 rounded-full bg-slate-100" />
                )}

                <div className="flex items-center justify-between">
                  {[
                    { label: 'On Track', value: sla?.on_track ?? 0, color: SLA_COLORS.on_track },
                    { label: 'Warning', value: sla?.warning ?? 0, color: SLA_COLORS.warning },
                    { label: 'Breached', value: sla?.breached ?? 0, color: SLA_COLORS.breached },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-1.5">
                      <div
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-xs text-slate-600">{item.label}</span>
                      <span className="text-xs font-semibold text-slate-800">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </ChartCard>
          </div>

          {/* Market Distribution */}
          <div key={`market-${filters.animationKey}`} className="flex-1 min-h-0">
            <ChartCard
              icon={Globe}
              title="By Market"
              subtitle="Campaign distribution across all markets"
              className="h-full"
            >
              {marketBreakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={marketBreakdown}
                    margin={{ top: 4, right: 4, left: 4, bottom: 4 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#cbd5e1"
                      strokeWidth={1}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="market"
                      tick={(props: XAxisTickContentProps) => {
                        const { x, y, payload } = props;
                        return (
                          <g transform={`translate(${x},${Number(y) + 8})`}>
                            <foreignObject x={-14} y={0} width={28} height={20}>
                              <div className="flex flex-col items-center">
                                <FlagIcon
                                  countryCode={payload.value}
                                  className="w-3.5 h-auto rounded-[2px]"
                                />
                              </div>
                            </foreignObject>
                          </g>
                        );
                      }}
                      axisLine={false}
                      tickLine={false}
                      height={28}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: '#94a3b8' }}
                      axisLine={false}
                      tickLine={false}
                      width={30}
                      allowDecimals={false}
                    />
                    <RechartsTooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const entry = payload[0].payload as { market: string; count: number };
                        const mTotal = marketBreakdown.reduce((sum, m) => sum + m.count, 0);
                        const pct = mTotal > 0 ? ((entry.count / mTotal) * 100).toFixed(1) : '0.0';
                        return (
                          <div className="bg-slate-800 text-white p-3 rounded-lg shadow-xl border border-slate-600">
                            <div className="flex items-center gap-2 font-semibold mb-2">
                              <FlagIcon
                                countryCode={entry.market}
                                className="w-4 h-auto rounded-[2px]"
                              />
                              {entry.market}
                            </div>
                            <div className="flex items-center justify-between gap-4 text-sm">
                              <span className="text-slate-300">Campaigns:</span>
                              <span className="font-mono font-medium">
                                {entry.count.toLocaleString()}
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-4 text-sm">
                              <span className="text-slate-300">Share:</span>
                              <span className="font-mono font-medium">{pct}%</span>
                            </div>
                          </div>
                        );
                      }}
                    />
                    <Bar
                      dataKey="count"
                      fill="#93c5fd"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={20}
                      animationBegin={200}
                      animationDuration={800}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                  No market data
                </div>
              )}
            </ChartCard>
          </div>
        </div>
      </div>
    </div>
  );
}
